import React, { lazy } from "react";

export interface NavigationRouteProps {
  /**
   * 页面唯一标识，写法和页面路径一致，对应菜单的key，tab页的key
   */
  path: string;
  /**
   * 菜单名称
   */
  name: string;
  /**
   * 页面组件路径，如 './home'
   */
  component?:
    | React.LazyExoticComponent<React.ComponentType>
    | React.ComponentType;
  children?: NavigationRouteProps[];
  icon?: React.ReactNode;
  // 是否隐藏于menu，默认不隐藏
  hideInMenu?: boolean;
  // 点击菜单时是否在新窗口打开
  openInNewWindow?: boolean;
}

const navigationRoutes: NavigationRouteProps[] = [
  {
    path: "index",
    name: "首页",
    children: [
      {
        path: "aiChat",
        name: "AI聊天",
        component: React.lazy(() => import("./pages/aiChat")),
      },
      {
        path: "aiWeeklyReport",
        name: "智能分析",
        component: React.lazy(() => import("./pages/aiWeeklyReport")),
      },
      {
        path: "aiCoachChat",
        name: "AI军师",
        component: React.lazy(() => import("./pages/aiCoachChat")),
      },
      {
        path: "homeDashboard",
        name: "数据大屏",
        component: React.lazy(() => import("./pages/homeDashboard")),
      },
      {
        path: "apiConfig",
        name: "运维接口",
        component: React.lazy(() => import("./pages/apiConfig")),
      },
      {
        path: "opsAssistant",
        name: "运维助手",
        component: React.lazy(() => import("./pages/opsAssistant")),
      },
      {
        path: "opsLogin",
        name: "运维登录",
        component: React.lazy(() => import("./pages/opsLogin")),
        hideInMenu: true,
      }
    ]
  },
  {
    path: "system",
    name: "系统管理",
    children: [
      {
        path: "configs",
        name: "参数配置",
        component: React.lazy(() => import("./pages/config")),
      },
      {
        path: "poolSettings",
        name: "公共池配置",
        component: React.lazy(() => import("./pages/poolSetting")),
      },
      {
        path: "dicts",
        name: "字典管理",
        component: React.lazy(() => import("./pages/dict")),
      },
      {
        path: "roles",
        name: "角色管理",
        component: React.lazy(() => import("./pages/role")),
      },
      {
        path: "contacts",
        name: "通讯录",
        component: React.lazy(() => import("./pages/contacts")),
      },
      {
        path: "users",
        name: "用户管理",
        component: React.lazy(() => import("./pages/user")),
      },
      {
        path: "userProfile",
        name: "销售画像",
        component: React.lazy(() => import("./pages/userProfile")),
        hideInMenu: true,
      },
      {
        path: "teams",
        name: "团队管理",
        component: React.lazy(() => import("./pages/team")),
      },
      {
        path: "clientLogs",
        name: "客户端日志",
        component: React.lazy(() => import("./pages/clientLog")),
      },
      {
        path: "loginLogs",
        name: "登录日志",
        component: React.lazy(() => import("./pages/loginLog")),
      },
      {
        path: "userOperationLog",
        name: "操作日志",
        component: React.lazy(() => import("./pages/userOperationLog")),
      },
      {
        path: "teamManager",
        name: "团队负责人",
        component: React.lazy(() => import("./pages/teamManager")),
      },
      {
        path: "redisManager",
        name: "缓存管理",
        component: React.lazy(() => import("./pages/redisManager")),
      },
      {
        path: "scheduledTask",
        name: "定时任务",
        component: React.lazy(() => import("./pages/scheduledTask")),
      },
      {
        path: "manualCenter",
        name: "操作手册",
        component: React.lazy(() => import("./pages/manual")),
        openInNewWindow: true,
      },
      {
        path: "logViewer",
        name: "日志查看",
        component: React.lazy(() => import("./pages/logViewer")),
      },
      {
        path: "scoringKeywordConfig",
        name: "评分词库",
        component: React.lazy(() => import("./pages/leads/scoringKeywordConfig")),
      },
      {
        path: "llmConfig",
        name: "LLM配置",
        component: React.lazy(() => import("./pages/leads/llmConfig")),
      },
      {
        path: "intentRuleConfig",
        name: "意向规则",
        component: React.lazy(() => import("./pages/leads/intentRuleConfig")),
      },
    ],
  },

  {
    path: "leadsmg",
    name: "客户",
    children: [
      {
        path: "saleNewLeads",
        name: "我的客户",
        component: React.lazy(() => import("./pages/leads/saleNewLead")),
      },
      {
        path: "saleLeads",
        name: "再分配客户",
        component: React.lazy(() => import("./pages/leads/saleLead")),
      },
      {
        path: "saleFavLeads",
        name: "重要客户",
        component: React.lazy(() => import("./pages/leads/saleFavLead")),
      },
      {
        path: "findLeads",
        name: "查客户",
        component: React.lazy(() => import("./pages/leads/findLead")),
      },
      {
        path: "teamLeads",
        name: "团队客户",
        component: React.lazy(() => import("./pages/leads/teamLead")),
      },
      {
        path: "pool",
        name: "公共池",
        component: React.lazy(() => import("./pages/leads/poolLead")),
      },
      {
        path: "leadAssign",
        name: "客户分配",
        component: React.lazy(() => import("./pages/leads/leadAssign")),
      },
      {
        path: "teamMembers",
        name: "团队成员",
        component: React.lazy(() => import("./pages/teamMember")),
      },
      {
        path: "leadVisitSignLog",
        name: "上门登记",
        component: React.lazy(() => import("./pages/leadVisitSignLog")),
      },
      {
        path: "waste",
        name: "废物池",
        component: React.lazy(() => import("./pages/leads/wasteLead")),
      },
      {
        path: "reject",
        name: "撞库客户",
        component: React.lazy(() => import("./pages/leads/rejectLead")),
      },
       {
        path: "cancellation",
        name: "退单客户",
        component: React.lazy(() => import("./pages/leads/cancellationLead")),
      },
      
      {
        path: "leadDetailNew",
        name: "客户详情",
        component: React.lazy(() => import("./pages/leads/leadDetailNewOut")),
        hideInMenu: true,
      },
      {
        path: "leadProfile",
        name: "客户画像",
        component: React.lazy(() => import("./pages/leads/leadProfileOut")),
        hideInMenu: true,
      },
      {
        path: "poolScoring",
        name: "客户评分",
        component: React.lazy(() => import("./pages/leads/poolScoring")),
      },
      {
        path: "aiCustomerAnalysis",
        name: "AI客户分析",
        component: React.lazy(() => import("./pages/leads/aiCustomerAnalysis")),
      },
      {
        path: "leadExactScoring",
        name: "客户评分(精确)",
        component: React.lazy(() => import("./pages/leads/leadExactScoring")),
      },
      {
        path: "callRecord",
        name: "拨号录音",
        component: React.lazy(() => import("./pages/leads/callRecord")),
      },
    ],
  },

  {
    path: "loan",
    name: "按揭",
    children: [
      {
        path: "loans",
        name: "全部按揭",
        component: React.lazy(() => import("./pages/loan/allLoan")),
      },
      {
        path: "notAssignLoans",
        name: "待添加按揭",
        component: React.lazy(() => import("./pages/loan/notAssignLoan")),
      },
      {
        path: "myLoan",
        name: "我的按揭",
        component: React.lazy(() => import("./pages/loan/myLoan")),
      },
      {
        path: "teamLoan",
        name: "团队按揭",
        component: React.lazy(() => import("./pages/loan/teamLoan")),
      },
      {
        path: "mortgageLead",
        name: "还款客户",
        component: React.lazy(() => import("./pages/mortgageLoan/indexMortgageLead")),
      },
      {
        path: "repaymentPlan",
        name: "还款计划",
        component: React.lazy(() => import("./pages/mortgageLoan/indexRepaymentPlan")),
      },
      {
        path: "paidPlan",
        name: "出款明细",
        component: React.lazy(() => import("./pages/mortgageLoan/indexOutMoneyPlan")),
      },
      {
        path: "paidPlanByDate",
        name: "每日出款",
        component: React.lazy(() => import("./pages/mortgageLoan/indexPaidPlanByDate")),
      },
      {
        path: "statOutMoneyPlanAll",
        name: "出款金额统计",
        component: React.lazy(() => import("./pages/mortgageLoan/indexStatOutMoneyPlanLeadAndCustomer")),
      },
      {
        path: "statOutMoneyPlan",
        name: "出款金额统计(出款人)",
        component: React.lazy(() => import("./pages/mortgageLoan/indexStatOutMoneyPlan")),
      },
      {
        path: "statOutMoneyPlanLead",
        name: "出款金额统计(客户)",
        component: React.lazy(() => import("./pages/mortgageLoan/indexStatOutMoneyPlanLead")),
      },
      {
        path: "statOutMoneyAnalysis",
        name: "出款金额分析",
        component: React.lazy(() => import("./pages/mortgageLoan/statOutMoneyByMonth")),
      },
      {
        path: "loanAnomalyMonitor",
        name: "贷款异常监控",
        component: React.lazy(() => import("./pages/mortgageLoan/loanAnomalyMonitor")),
      },
      
      {
        path: "mortgageLeadSettle",
        name: "释放客户",
        component: React.lazy(() => import("./pages/mortgageLoan/indexMortgageLeadSettle")),
      },
      {
        path: "paidPlanSettle",
        name: "出款明细(释放)",
        component: React.lazy(() => import("./pages/mortgageLoan/indexPaidPlanSettle")),
      },
       {
        path: "monthPlanRepaymentReport",
        name: "月待还汇总",
        component: React.lazy(() => import("./pages/mortgageLoanReport/planRepaymentReportAgentAndCustomer")),
      },

      {
        path: "mortgageLoanQuery",
        name: "贷款查询",
        component: React.lazy(() => import("./pages/mortgageLoan/indexMortgageLoanQuery")),
      },
      {
        path: "repaymentPlanQuery",
        name: "还款查询",
        component: React.lazy(() => import("./pages/mortgageLoan/indexRepaymentPlanQuery")),
      },
      {
        path: "leadExt",
        name: "实际利息",
        component: React.lazy(() => import("./pages/mortgageLoan/indexLeadExt")),
      },
      {
        path: "reconciliationAll",
        name: "对账管理",
        component: React.lazy(() => import("./pages/mortgageLoan/indexReconciliationByLeadAndCustomer")),
      },
      {
        path: "reconciliationAll",
        name: "对账管理",
        component: React.lazy(() => import("./pages/mortgageLoan/indexStatOutMoneyPlanLeadAndCustomer")),
      },
      {
        path: "reconciliation",
        name: "出款人对账",
        component: React.lazy(() => import("./pages/mortgageLoan/indexReconciliation")),
      },
      {
        path: "reconciliationByLead",
        name: "客户对账",
        component: React.lazy(() => import("./pages/mortgageLoan/indexReconciliationByLead")),
      },
    ],
  },

  {
    path: "maintain",
    name: "运营",
    children: [
      {
        path: "products",
        name: "产品管理",
        component: React.lazy(() => import("./pages/product")),
      },
      {
        path: "bankTrustEnterprise",
        name: "易贷通企业",
        component: React.lazy(() => import("./pages/bankTrustEnterprise")),
      },
      {
        path: "contracts",
        name: "合同管理",
        component: React.lazy(() => import("./pages/contract")),
      },
    ],
  },

  {
    path: "flow",
    name: "流量",
    children: [
      {
        path: "leads",
        name: "全部客户",
        component: React.lazy(() => import("./pages/leads")),
      },
      {
        path: "trafficApps",
        name: "渠道APP",
        component: React.lazy(() => import("./pages/trafficApp")),
      },
      {
        path: "leadMd5Mobile",
        name: "手机号排重包",
        component: React.lazy(() => import("./pages/config/leadMd5Mobile")),
      },
      {
        path: "leadImportLog",
        name: "客户导入日志",
        component: React.lazy(() => import("./pages/leadImportLog")),
      },
      {
        path: "leadImportHit",
        name: "客户导入撞库",
        component: React.lazy(() => import("./pages/leadImportLog/indexHit")),
      },
      {
        path: "leadCheckEmptyMobile",
        name: "空号检测统计",
        component: React.lazy(() => import("./pages/leadCheckEmptyMobileLog")),
      },
      {
        path: "leadWarehouse",
        name: "客户底库",
        component: React.lazy(() => import("./pages/leadWarehouse")),
      },
      {
        path: "leadFeedbackLog",
        name: "星级反馈",
        component: React.lazy(() => import("./pages/leadFeedbackLog")),
      },
    ],
  },

  {
    path: "report",
    name: "报表",
    children: [
      {
        path: "teamPortrait",
        name: "销售画像",
        component: React.lazy(() => import("./pages/teamPortrait")),
        openInNewWindow: true,
      },
      {
        path: "orgUserStarStat",
        name: "团队数据跟进报表",
        component: React.lazy(() => import("./pages/stat/orgUserStarStat")),
      },
      {
        path: "orgUserStarStatPersonal",
        name: "成员数据跟进报表",
        component: React.lazy(
          () => import("./pages/stat/orgUserStarStatPersonal")
        ),
      },
      {
        path: "orgUserNewLeadStarStat",
        name: "新客数据跟进报表",
        component: React.lazy(() => import("./pages/stat/orgUserNewLeadStarStat")),
      },
      {
        path: "sourceStarStat",
        name: "渠道对比报表",
        component: React.lazy(() => import("./pages/stat/sourceStarStat")),
      },
      {
        path: "sourceStarStatDkhit",
        name: "渠道对比报表(底)",
        component: React.lazy(() => import("./pages/stat/sourceStarStatDkhit")),
      },
      {
        path: "againAssignStarStat",
        name: "再分配客户跟进报表",
        component: React.lazy(() => import("./pages/stat/againAssignStarStat")),
      },
      {
        path: "assignVisitStat",
        name: "团队KPI报表",
        component: React.lazy(() => import("./pages/stat/assignVisitStat")),
      },
      {
        path: "assignVisitStatDate",
        name: "团队日期KPI报表",
        component: React.lazy(() => import("./pages/stat/assignVisitStatDate")),
      },
      {
        path: "assignVisitStatPersonal",
        name: "成员KPI报表",
        component: React.lazy(
          () => import("./pages/stat/assignVisitStatPersonal")
        ),
      },
      {
        path: "customerTouchStat",
        name: "客户触达分析",
        component: React.lazy(() => import("./pages/stat/customerTouchStat")),
      },
      {
        path: "callStat",
        name: "通话统计",
        component: React.lazy(() => import("./pages/stat/callStat")),
      },
    ],
  },

];

export default navigationRoutes;
